import { Link } from '@tanstack/react-router';
import { AlertOctagon, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/cn';
import { usePosAccess } from '../api/hooks';

/**
 * Sticky top banner when the business is out of good standing.
 *
 *  - trial ended / suspended / canceled → red banner, POS access is off; Pay now
 *    walks the owner to the invoices page to settle the due invoice (SSLCommerz).
 *  - past_due → amber warning; access continues through the dunning grace window.
 *
 * `canPay` renders the Pay-now button (owner shell). Staff can't pay — they get
 * the message and a nudge to contact their admin.
 */
export function SubscriptionGateBanner({ businessId, canPay = true }: { businessId: string | null | undefined; canPay?: boolean }) {
  const { blocked, pastDue, reason } = usePosAccess(businessId);
  if (!blocked && !pastDue) return null;

  const Icon = blocked ? AlertOctagon : AlertTriangle;
  const text = blocked
    ? reason === 'trial_ended'
      ? 'Your free trial has ended — POS access is paused until the first invoice is paid.'
      : 'Your subscription is not active — POS access is paused until the due invoice is paid.'
    : 'Your latest invoice is overdue. Pay now to avoid POS access being suspended.';

  return (
    <div
      role="alert"
      className={cn(
        'w-full border-b border-zinc-200 dark:border-zinc-700 px-4 sm:px-5 py-2 flex items-center gap-3 text-sm',
        blocked
          ? 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-100'
          : 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100',
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">{text}</span>
        {!canPay && <span> Ask your business admin to settle the bill.</span>}
      </div>
      {canPay && (
        <Link
          to="/billing"
          className={cn(
            'flex-shrink-0 px-3 py-1 rounded-md text-xs font-semibold transition',
            blocked
              ? 'bg-rose-600 text-white hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-400 dark:text-rose-950'
              : 'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-amber-950',
          )}
        >
          Pay now
        </Link>
      )}
    </div>
  );
}
